import { useMemo, useState } from "react";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import { imageUrl } from "@a2z/ui/media/image-url";
import { MEDIA_BASE_URL } from "@/lib/media-base";
import { Modal, Button, useToast } from "@a2z/ui";
import { CheckoutLinkProductPicker } from "@/modules/checkout-links/components/CheckoutLinkProductPicker";
import { CheckoutLinkResult } from "@/modules/checkout-links/components/CheckoutLinkResult";
import { useCreateMultiCheckoutLinkMutation } from "@/modules/checkout-links/services/useCreateMultiCheckoutLinkMutation";
import { matchGroup, COLOR_GROUP_NAMES, swatchHex } from "@/modules/products/lib/variantsDisplay";
import type { Locale } from "@/lib/i18n/config";
import type {
  SellerProductListItemView,
  ProductVariantView,
} from "@/lib/api/types";

type Props = {
  storeSlug: string;
  products: SellerProductListItemView[];
  onClose: () => void;
};

/** One cart line: a product, the chosen option value per group, and quantity. */
type CartLine = {
  product: SellerProductListItemView;
  /** optionGroupId → optionValueId */
  selections: Record<string, string>;
  quantity: number;
};

const MAX_QTY = 10;
const MAX_ITEMS = 20;

function thumbOf(product: SellerProductListItemView): string | null {
  const ready = product.images.find((img) => img.status === "READY");
  if (!ready?.objectKey) return null;
  return imageUrl(
    ready.objectKey,
    { width: 96, format: "auto", removeBackground: ready.useBgRemoved === true },
    { baseUrl: MEDIA_BASE_URL },
  );
}

/** Default option selections, taken from the product's default variant. */
function defaultSelections(product: SellerProductListItemView): Record<string, string> {
  const def =
    product.variants.find((v) => v.id === product.defaultVariantId) ??
    product.variants.find((v) => v.isDefault) ??
    product.variants[0];
  const map: Record<string, string> = {};
  def?.selections.forEach((s) => {
    map[s.optionGroupId] = s.optionValueId;
  });
  return map;
}

/** Resolve the variant whose selections match every chosen option value. */
function resolveVariant(
  product: SellerProductListItemView,
  selections: Record<string, string>,
): ProductVariantView | null {
  if (product.optionGroups.length === 0) return product.variants[0] ?? null;
  return (
    product.variants.find(
      (v) =>
        v.selections.length > 0 &&
        v.selections.every((s) => selections[s.optionGroupId] === s.optionValueId),
    ) ?? null
  );
}

function toLine(product: SellerProductListItemView): CartLine {
  return { product, selections: defaultSelections(product), quantity: 1 };
}

export function ProductsCartConfigModal({ storeSlug, products, onClose }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const { mutateAsync: createLink, isPending } = useCreateMultiCheckoutLinkMutation();
  const [result, setResult] = useState<{ url: string; expiresAt: string } | null>(null);

  const [lines, setLines] = useState<CartLine[]>(() => products.map(toLine));

  const selectedIds = useMemo(() => new Set(lines.map((l) => l.product.id)), [lines]);

  const addProduct = (product: SellerProductListItemView) => {
    if (selectedIds.has(product.id) || lines.length >= MAX_ITEMS) return;
    setLines((prev) => [...prev, toLine(product)]);
  };
  const removeProduct = (productId: string) =>
    setLines((prev) => prev.filter((l) => l.product.id !== productId));
  const setSelection = (productId: string, groupId: string, valueId: string) =>
    setLines((prev) =>
      prev.map((l) =>
        l.product.id === productId
          ? { ...l, selections: { ...l.selections, [groupId]: valueId } }
          : l,
      ),
    );
  const setQuantity = (productId: string, quantity: number) =>
    setLines((prev) =>
      prev.map((l) => (l.product.id === productId ? { ...l, quantity } : l)),
    );

  // A line is invalid when its product has options but the chosen combination
  // maps to no real variant — block generation until every line resolves.
  const resolved = lines.map((l) => ({ line: l, variant: resolveVariant(l.product, l.selections) }));
  const hasInvalid = resolved.some(
    (r) => r.line.product.optionGroups.length > 0 && r.variant === null,
  );
  const canGenerate = lines.length > 0 && !hasInvalid;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    try {
      const response = await createLink({
        storeSlug,
        items: resolved.map((r) => ({
          productId: r.line.product.id,
          productVariantId: r.variant?.id,
          quantity: r.line.quantity,
        })),
      });
      setResult({ url: response.url, expiresAt: response.expiresAt });
      try {
        await navigator.clipboard.writeText(response.url);
        toast.success(t(keys.checkoutLinks.generatedToast));
      } catch {
        // Clipboard may be unavailable; the result view has its own copy button.
      }
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={result ? t(keys.products.bulkCartReadyTitle) : t(keys.products.cartConfigTitle)}
      closeLabel={t(keys.common.close)}
      hideFooter
    >
      {result ? (
        <CheckoutLinkResult url={result.url} expiresAt={result.expiresAt} />
      ) : (
        <div className="flex flex-col gap-4">
          {/* Search → add more products to the cart */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[color:var(--text-muted)]">
              {t(keys.products.cartAddProductsLabel)}
            </span>
            <CheckoutLinkProductPicker
              storeSlug={storeSlug}
              selectedIds={selectedIds}
              totalItemCount={lines.length}
              onAdd={addProduct}
            />
          </div>

          {/* Cart lines */}
          {lines.length === 0 ? (
            <p className="py-6 text-center text-sm text-[color:var(--text-muted)]">
              {t(keys.products.cartEmptyHint)}
            </p>
          ) : (
            <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
              {resolved.map(({ line, variant }) => (
                <CartLineRow
                  key={line.product.id}
                  line={line}
                  variant={variant}
                  locale={locale}
                  onRemove={() => removeProduct(line.product.id)}
                  onSelect={(groupId, valueId) => setSelection(line.product.id, groupId, valueId)}
                  onQuantity={(q) => setQuantity(line.product.id, q)}
                  unavailableLabel={t(keys.products.cartComboUnavailable)}
                  decrementLabel={t(keys.products.checkoutLinkStepperDecrement)}
                  incrementLabel={t(keys.products.checkoutLinkStepperIncrement)}
                  removeLabel={t(keys.common.remove)}
                />
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isPending || !canGenerate}
            onClick={() => void handleGenerate()}
          >
            {isPending ? t(keys.common.loading) : t(keys.checkoutLinks.generateButton)}
          </Button>
        </div>
      )}
    </Modal>
  );
}

// ─── Cart line row ────────────────────────────────────────────────────────────

function CartLineRow({
  line,
  variant,
  locale,
  onRemove,
  onSelect,
  onQuantity,
  unavailableLabel,
  decrementLabel,
  incrementLabel,
  removeLabel,
}: {
  line: CartLine;
  variant: ProductVariantView | null;
  locale: Locale;
  onRemove: () => void;
  onSelect: (groupId: string, valueId: string) => void;
  onQuantity: (quantity: number) => void;
  unavailableLabel: string;
  decrementLabel: string;
  incrementLabel: string;
  removeLabel: string;
}) {
  const { product, selections, quantity } = line;
  const thumb = thumbOf(product);
  const invalid = product.optionGroups.length > 0 && variant === null;

  return (
    <div className="flex gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3">
      {thumb ? (
        <img
          src={thumb}
          alt=""
          aria-hidden="true"
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="h-12 w-12 shrink-0 rounded-lg bg-[color:var(--background-muted)]" />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-[color:var(--text-primary)]">
            {pickLocalized(locale, product.nameAr, product.nameEn)}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <QtyButton label={decrementLabel} disabled={quantity <= 1} onClick={() => onQuantity(Math.max(1, quantity - 1))}>
              −
            </QtyButton>
            <span className="w-7 text-center text-sm font-medium text-[color:var(--text-primary)]">
              {quantity}
            </span>
            <QtyButton label={incrementLabel} disabled={quantity >= MAX_QTY} onClick={() => onQuantity(Math.min(MAX_QTY, quantity + 1))}>
              +
            </QtyButton>
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeLabel}
              className="ms-1 flex h-8 w-8 items-center justify-center rounded-lg text-[color:var(--status-error)] transition-colors hover:bg-[color:var(--background-muted)]"
            >
              ×
            </button>
          </div>
        </div>

        {/* Full variant selection — one row of chips per option group */}
        {product.optionGroups.map((group) => {
          const isColor = matchGroup(group, COLOR_GROUP_NAMES);
          return (
            <div key={group.id} className="flex flex-col gap-1">
              <span className="text-[11px] font-medium text-[color:var(--text-muted)]">
                {pickLocalized(locale, group.nameAr, group.nameEn)}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {group.values.map((value) => {
                  const selected = selections[group.id] === value.id;
                  const label = pickLocalized(locale, value.valueAr, value.valueEn);
                  if (isColor) {
                    return (
                      <button
                        key={value.id}
                        type="button"
                        title={label}
                        aria-label={label}
                        aria-pressed={selected}
                        onClick={() => onSelect(group.id, value.id)}
                        className={[
                          "h-9 w-9 rounded-full border transition-transform lg:h-7 lg:w-7",
                          selected
                            ? "border-[color:var(--color-hero-surface)] ring-2 ring-[color:var(--color-hero-surface)] ring-offset-1"
                            : "border-[color:var(--border-strong)] hover:scale-105",
                        ].join(" ")}
                        style={{
                          background: swatchHex({
                            colorHex: value.colorHex,
                            valueEn: value.valueEn,
                            valueAr: value.valueAr,
                          }),
                        }}
                      />
                    );
                  }
                  return (
                    <button
                      key={value.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => onSelect(group.id, value.id)}
                      className={[
                        "min-w-[36px] rounded-lg border px-2.5 py-1 text-[12px] font-medium transition-colors",
                        selected
                          ? "border-[color:var(--color-hero-surface)] bg-[color:var(--color-hero-surface)] text-white"
                          : "border-[color:var(--border-light)] text-[color:var(--text-secondary)] hover:border-[color:var(--text-muted)]",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {invalid ? (
          <span className="text-[11px] font-medium text-[color:var(--status-error)]">
            {unavailableLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function QtyButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-10 w-10 items-center justify-center rounded-lg border border-[color:var(--border-light)] text-[color:var(--text-secondary)] transition-colors active:bg-[color:var(--background-muted)] disabled:opacity-40 lg:h-8 lg:w-8"
    >
      {children}
    </button>
  );
}
